import {
  reportDefinitionsApi,
  type ReportDefinitionGroup,
  type ReportDefinitionSummary,
  type ReportDefinitionTenant,
} from './api';
import { ApiFailure } from './api-failure';
import { navigation } from './navigation.svelte';
import { ReportGroupOverview } from './report-group-overview';
import { UndoOffer } from './undo-offer.svelte';

/**
 * A tela de relatórios: a lista de conjuntos em `/relatorios`, as linhas de cliente de um conjunto em
 * `/relatorios/<conjunto>`, a linha aberta em `/relatorios/<conjunto>/<linha>` e os envios dela em
 * `/relatorios/<conjunto>/<linha>/envios`.
 * O aviso com Desfazer mora aqui porque sobrevive à gaveta que o disparou.
 */
export class ReportDefinitionsBoard {
  static readonly PATH = '/relatorios';
  private static readonly ROUTE = /^\/relatorios\/(\d+)(?:\/(\d+)(\/envios)?)?\/?$/;

  groups = $state<ReportDefinitionGroup[] | null>(null);
  definitions = $state<ReportDefinitionSummary[]>([]);
  tenants = $state<ReportDefinitionTenant[]>([]);
  error = $state<string | null>(null);
  readonly undo = new UndoOffer();

  private readonly route = $derived(navigation.path.match(ReportDefinitionsBoard.ROUTE));
  readonly openedGroup = $derived(this.groups?.find((group) => String(group.id) === this.route?.[1]) ?? null);
  readonly openedLine = $derived(
    this.openedGroup ? (this.linesOf(this.openedGroup).find((definition) => String(definition.id) === this.route?.[2]) ?? null) : null,
  );
  readonly isShowingSends = $derived(this.openedLine !== null && this.route?.[3] !== undefined);
  readonly overviews = $derived((this.groups ?? []).map((group) => new ReportGroupOverview(group, this.linesOf(group))));

  constructor(private readonly onUnauthorized: () => void) {}

  linesOf(group: ReportDefinitionGroup): ReportDefinitionSummary[] {
    return this.definitions.filter((definition) => definition.reportDefinitionGroupId === group.id);
  }

  isInGroup(group: ReportDefinitionGroup, tenant: ReportDefinitionTenant): boolean {
    return this.linesOf(group).some((definition) => definition.tenantId === tenant.tenantId);
  }

  /** Conjuntos vêm da API e linhas do banco: uma fonte fora do ar não apaga o que a outra trouxe. */
  async load(): Promise<void> {
    const [groups, definitions] = await Promise.allSettled([reportDefinitionsApi.groups(), reportDefinitionsApi.list()]);
    this.error = null;
    if (groups.status === 'fulfilled') this.groups = groups.value;
    if (definitions.status === 'fulfilled') this.definitions = definitions.value;
    if (groups.status === 'rejected') this.fail(groups.reason);
    if (definitions.status === 'rejected') this.fail(definitions.reason);
  }

  async loadTenants(): Promise<void> {
    try {
      this.tenants = await reportDefinitionsApi.tenants();
    } catch (err) {
      this.fail(err);
    }
  }

  openGroup(groupId: number): void {
    navigation.goTo(`${ReportDefinitionsBoard.PATH}/${groupId}`);
  }

  openLine(definition: ReportDefinitionSummary): void {
    navigation.goTo(`${ReportDefinitionsBoard.PATH}/${definition.reportDefinitionGroupId}/${definition.id}`);
  }

  openSends(definition: ReportDefinitionSummary): void {
    navigation.goTo(`${ReportDefinitionsBoard.PATH}/${definition.reportDefinitionGroupId}/${definition.id}/envios`);
  }

  backToGroups(): void {
    navigation.goTo(ReportDefinitionsBoard.PATH);
  }

  fail(err: unknown): void {
    this.error = ApiFailure.messageOf(err, this.onUnauthorized);
  }
}
